/**
 * DocumentEditorPage - Document editor with sequential vertical layout
 * Layout: Toolbar (left) | Header + Document Content + Play Library Panel (right, retractable)
 *
 * Uses block architecture: document -> section blocks -> play_reference blocks
 * Sequential vertical flow (not free-form canvas).
 */

import './document-editor-page.css'
import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Loading } from '@/components/ui/Loading'
import { useResolvedDocument } from '@/hooks/UseResolvedDocument'
import { useDocumentMutations } from '@/hooks/useDocumentBlocks'
import { useDocumentZoom } from '@/components/document-editor/hooks/UseDocumentZoom'
import { DocumentToolbar } from '@/components/document-editor/DocumentToolbar'
import { DocumentHeader } from '@/components/document-editor/DocumentHeader'
import { DocumentCanvas } from '@/components/document-editor/DocumentCanvas'
import { PlayLibraryPanel } from '@/components/document-editor/PlayLibraryPanel'
import {
	getSectionsFromDocument,
	getPlaysFromSection,
	type SectionItem,
	type PlayItem,
} from '@/components/document-editor/Types'

/** Default color for new sections - Tailwind blue-500 */
const DEFAULT_SECTION_COLOR = '#3B82F6'

export function DocumentEditorPage() {
	const { documentId } = useParams<{ documentId: string }>()
	const id = documentId ? documentId : null

	// Use 'resolvedDoc' to avoid shadowing window.document
	const { document: resolvedDoc, playbookId, isLoading, error } = useResolvedDocument(id)
	const {
		createSection,
		updateDocument,
		addPlayReference,
		removePlayReference,
		undo,
		redo,
		canUndo,
		canRedo,
	} = useDocumentMutations(id)

	// Zoom controls with trackpad gesture support
	const { zoomLevel, zoomIn, zoomOut, onWheel } = useDocumentZoom(1.0)
	const canvasAreaRef = useRef<HTMLDivElement>(null)

	// UI state
	const [showPlayLibrary, setShowPlayLibrary] = useState(true)
	const [showGrid] = useState(false)
	const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)
	const [isFlagMode, setIsFlagMode] = useState(false)

	// Transform resolved document into editor-friendly format
	const sections = useMemo<SectionItem[]>(() => {
		return getSectionsFromDocument(resolvedDoc)
	}, [resolvedDoc])

	// Extract all play references from sections
	const plays = useMemo<PlayItem[]>(() => {
		const allPlays: PlayItem[] = []
		sections.forEach(section => {
			const sectionPlays = getPlaysFromSection(section)
			allPlays.push(...sectionPlays)
		})
		return allPlays
	}, [sections])

	// Handlers
	const handleAddPlayToSection = useCallback((
		sectionId: string,
		playId: string,
		_columnIndex: number,
		_rowIndex: number
	) => {
		addPlayReference(sectionId, playId)
			.then(() => toast.success('Play added to section'))
			.catch((err: Error) => toast.error(err.message))
	}, [addPlayReference])

	const handleRemovePlay = useCallback((playBlockId: string) => {
		removePlayReference(playBlockId)
			.then(() => toast.success('Play removed'))
			.catch((err: Error) => toast.error(err.message))
	}, [removePlayReference])

	const handleUpdateName = useCallback((name: string) => {
		updateDocument({ name })
			.catch((err: unknown) => {
				const message = err instanceof Error ? err.message : 'Failed to update document name'
				toast.error(message)
			})
	}, [updateDocument])

	// Toolbar handlers
	const handleAddSection = useCallback(() => {
		// Generate a default section name based on existing sections count
		const sectionNumber = sections.length + 1
		const defaultName = `Section ${sectionNumber}`

		createSection({
			name: defaultName,
			color: DEFAULT_SECTION_COLOR,
		})
			.then(() => toast.success('Section created'))
			.catch((err: unknown) => {
				const message = err instanceof Error ? err.message : 'Failed to create section'
				toast.error(message)
			})
	}, [createSection, sections.length])

	const handleUndo = useCallback(() => {
		undo().catch((err: unknown) => {
			console.error('Undo failed:', err)
			toast.error('Undo failed')
		})
	}, [undo])

	const handleRedo = useCallback(() => {
		redo().catch((err: unknown) => {
			console.error('Redo failed:', err)
			toast.error('Redo failed')
		})
	}, [redo])

	const handleToggleFlag = () => {
		setIsFlagMode(!isFlagMode)
	}

	const handleChangeSize = useCallback((_preset: string) => {
		// TODO: Persist canvas preset change via document properties update
	}, [])

	// Keyboard shortcuts for undo/redo
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
				e.preventDefault()
				if (e.shiftKey) {
					handleRedo()
				} else {
					handleUndo()
				}
			}
		}
		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [handleUndo, handleRedo])

	// Add trackpad gesture support for zoom
	useEffect(() => {
		const canvasArea = canvasAreaRef.current
		if (!canvasArea) return

		canvasArea.addEventListener('wheel', onWheel, { passive: false })
		return () => {
			canvasArea.removeEventListener('wheel', onWheel)
		}
	}, [onWheel])

	if (isLoading) {
		return <Loading text="Loading document..." fullscreen />
	}

	if (error || !resolvedDoc) {
		return (
			<div className="document-editor__error">
				<p>{error?.message ?? 'Document not found'}</p>
				<Button onClick={() => window.location.href = '/content'}>Back to Content</Button>
			</div>
		)
	}

	if (!playbookId) {
		return (
			<div className="document-editor__error">
				<p>Document playbook not found</p>
				<Button onClick={() => window.location.href = '/content'}>Back to Content</Button>
			</div>
		)
	}

	const canvasPreset = resolvedDoc.document.properties.canvasPreset ?? 'letter'

	return (
		<main className="document-editor">
			<DocumentToolbar
				canUndo={canUndo}
				canRedo={canRedo}
				onUndo={handleUndo}
				onRedo={handleRedo}
				onAddSection={handleAddSection}
				onZoomIn={zoomIn}
				onZoomOut={zoomOut}
				onToggleFlag={handleToggleFlag}
				isFlagMode={isFlagMode}
				canvasPreset={canvasPreset}
				onChangeSize={handleChangeSize}
			/>
			<div className="document-editor__content">
				<DocumentHeader
					document={resolvedDoc}
					showPanel={showPlayLibrary}
					onTogglePanel={() => setShowPlayLibrary(!showPlayLibrary)}
					onUpdateName={handleUpdateName}
				/>
				<div className="document-editor__main">
					<div ref={canvasAreaRef} className="document-editor__canvas-area">
						<DocumentCanvas
							canvasPreset={canvasPreset}
							sections={sections}
							plays={plays}
							zoomLevel={zoomLevel}
							showGrid={showGrid}
							selectedSectionId={selectedSectionId}
							onSelectSection={setSelectedSectionId}
							onMoveSection={() => {
								// No-op: sequential layout does not support free-form positioning
							}}
							onAddPlayToSection={handleAddPlayToSection}
							onRemovePlay={handleRemovePlay}
						/>
					</div>
					<PlayLibraryPanel playbookId={playbookId} showPanel={showPlayLibrary} />
				</div>
			</div>
		</main>
	)
}
